/**
 * Trajectory utility functions
 * Pure, framework-agnostic — safe to use in API, web and tests.
 */

/** Remove diacritics, spaces and special chars, uppercase */
export function sanitizeForCode(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\p{Diacritic}]/gu, "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
}

/**
 * Distribute 10 characters equitably across `count` slots.
 * Remainder characters go to the first slots.
 * e.g. count=2 → [5,5], count=3 → [4,3,3], count=10 → [1,1,...,1]
 */
export function computeAllocations(count: number): number[] {
  if (count <= 0) return []
  const result = Array<number>(count).fill(0)
  let remaining = 10
  let idx = 0
  while (remaining > 0) {
    result[idx % count]! += 1
    remaining -= 1
    idx += 1
  }
  return result
}

/**
 * Build a 10-char max code from an ordered list of city names.
 * Each city gets a proportional slice of the 10 available chars.
 */
export function computeTrajectoryCode(cities: { name: string }[]): string {
  if (cities.length === 0) return ""
  const sanitized = cities.map((c) => sanitizeForCode(c.name))
  const allocations = computeAllocations(cities.length)
  return sanitized
    .map((value, idx) => value.slice(0, allocations[idx] ?? 0))
    .join("")
    .slice(0, 10)
}

/**
 * Build a human-readable name joining all city names with " / ".
 */
export function computeTrajectoryName(cities: { name: string }[]): string {
  if (cities.length === 0) return ""
  return cities.map((c) => c.name).join(" / ")
}
